import * as React from "react";
import { cn } from "lib/utils";
import { Label } from "../label";
import { FormControl, FormItem, FormMessage } from "../form";
import { X } from "lucide-react"; // ícone de remover (shadcn/lucide)

export interface InputFileDragProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  onFileSelect?: (files: FileList | null) => void;
}

const InputFileDragGs = React.forwardRef<HTMLInputElement, InputFileDragProps>(
  ({ className, label, onFileSelect, onChange, ...props }, ref) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = () => {
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        setSelectedFiles(files);
        if (inputRef.current) {
          const dataTransfer = new DataTransfer();
          files.forEach((f) => dataTransfer.items.add(f));
          inputRef.current.files = dataTransfer.files;
        }
        onFileSelect?.(e.dataTransfer.files);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      setSelectedFiles(files);
      onFileSelect?.(e.target.files);
      onChange && onChange(e);
    };

    const handleClick = () => {
      inputRef.current?.click();
    };

    const handleRemoveFile = (index: number) => {
      const updated = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(updated);
      if (inputRef.current) {
        const dataTransfer = new DataTransfer();
        updated.forEach((f) => dataTransfer.items.add(f));
        inputRef.current.files = dataTransfer.files;
        onFileSelect?.(dataTransfer.files);
      }
    };

    return (
      <FormItem>
        <FormControl>
          <div className="grid w-full items-start gap-3">
            {label && (
              <Label className="font-GilroySemibold text-sm text-slate-800">
                {label}
              </Label>
            )}

            {/* Área de drag & drop */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClick}
              className={cn(
                "flex flex-col items-center justify-center w-full h-24 cursor-pointer rounded-lg border border-dashed transition-colors duration-200",
                isDragging
                  ? "border-primary-500 bg-primary-50"
                  : "border-slate-300 bg-slate-50 hover:bg-slate-100",
                className
              )}
            >
              <p className="text-sm text-slate-600 text-center">Arraste e solte arquivos nesta área ou clique para selecionar</p>
              <input
                type="file"
                multiple
                ref={(node) => {
                  inputRef.current = node;
                  if (typeof ref === "function") ref(node);
                  else if (ref)
                    (
                      ref as React.MutableRefObject<HTMLInputElement | null>
                    ).current = node;
                }}
                className="hidden"
                onChange={handleChange}
              />
            </div>

            {/* Lista de arquivos */}
            {selectedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm shadow-sm hover:shadow-md transition-shadow"
                  >
                    <span className="truncate max-w-[160px] text-slate-700">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <FormMessage />
          </div>
        </FormControl>
      </FormItem>
    );
  }
);

InputFileDragGs.displayName = "InputFileDragGs";

export { InputFileDragGs };
